import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { connect } from 'react-redux';
import {
  getFirms,
  deleteFirm,
  getFirmPaymentTypes,
} from 'store/requests/definitions';
import 'antd/dist/antd.css';
import { Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import { CSVLink } from 'react-csv';
import { ConfigProvider } from 'antd';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import { Tooltip, Row, Button, Table, Col, Form, Tag, Dropdown } from 'antd';
import {
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';

const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const FirmsTableFilters = React.lazy(() => import('./FirmsTableFilters'));
let lastQuery = false;

const FirmsView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const { confirm } = Modal;
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    getFirmPaymentTypes();
  }, []);

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const columns = [
    {
      title: t('firmName'),
      dataIndex: 'name',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('paymentType'),
      dataIndex: 'paymentType',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('currencyAccount'),
      dataIndex: 'currencyAccount',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('pricePerOrder'),
      dataIndex: 'pricePerOrder',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('legalTitle'),
      dataIndex: 'legalTitle',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('VKN'),
      dataIndex: 'taxNumber',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
                {
                  danger: true,
                  label: t('delete'),
                  icon: <DeleteOutlined />,
                  onClick: () => {
                    showDeleteConfirm(selectedItemId);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItemId(dataItem.id)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={'Firmalar'}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [item.name, item.paymentType]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  function showDeleteConfirm(itemId) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('firmDelete'),
      okText: t('delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteFirm(itemId).then((res) => {
          if (res.isSuccess) {
            notificationDelete('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('firmDeleted'),
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = 'filter=active==true';

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item]) {
          queryString += ` and ${item}=='${encodeURI(filters[item])}'`;
        }
      });
    }
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getFirms(queryString)
        .then((firm) => {
          const { _embedded, page } = firm;
          const { firmResponseDtoList } = _embedded ? _embedded : [];
          setRows(firmResponseDtoList ? firmResponseDtoList : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <FirmsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}

        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            firmId={selectedItemId}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default connect()(FirmsView);
